import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectTspOptions } from '../map/slice/mapSelectors';
import { useDebouncedCallback } from '../helpers/hooks';
import { selectAdvancedPathComponentFilters } from '../filters/advancedFiltersSelectors';
import { advancedFiltersActions } from '../filters/advancedFiltersSlice';
import { MultiSelectSearch } from '../helpers/MultiSelect';
import { useGetTSPsQuery } from '../services/edgeApi';

export type PathComponentFiltersProps = {
  lockedTsps?: number[];
};

export type PathComponentFiltersRef = {
  focusSearchBox: () => void;
  clearSearchBox: () => void;
};

export const PathComponentFilters = forwardRef<
  PathComponentFiltersRef,
  PathComponentFiltersProps
>(({ lockedTsps = [] }, ref) => {
  useGetTSPsQuery(undefined);
  const dispatch = useDispatch();
  const advancedPathFilters = useSelector(selectAdvancedPathComponentFilters);
  const searchBoxRef = React.useRef<HTMLInputElement>(null);
  const tspOptions = useSelector(selectTspOptions);

  const typeOptions = useMemo(
    () => [
      { value: 'point', label: 'Point' },
      { value: 'interconnect', label: 'Interconnect' },
      { value: 'pool', label: 'Pool' },
    ],
    [],
  );

  useEffect(() => {
    if (lockedTsps?.length > 0) {
      dispatch(
        advancedFiltersActions.setFilterValue({
          type: 'pathComponents',
          field: 'tsps',
          value: Array.from(new Set(lockedTsps)),
        }),
      );
      return () => {
        dispatch(
          advancedFiltersActions.setFilterValue({
            type: 'pathComponents',
            field: 'tsps',
            value: advancedPathFilters.tsps,
          }),
        );
      };
    }
    return;
  }, [lockedTsps]);

  const updateTextSearch = useDebouncedCallback(
    (value: string) =>
      dispatch(
        advancedFiltersActions.setFilterValue({
          type: 'pathComponents',
          field: 'textSearch',
          value,
        }),
      ),
    400,
  );

  useImperativeHandle(
    ref,
    () => ({
      focusSearchBox: () => {
        searchBoxRef.current?.focus();
      },
      clearSearchBox: () => {
        if (!searchBoxRef.current) return;
        searchBoxRef.current.value = '';
        updateTextSearch('');
      },
    }),
    [searchBoxRef.current, updateTextSearch],
  );

  return (
    <div className="my-2 p-3">
      <div className="row">
        <div className="col">
          <input
            ref={searchBoxRef}
            className="form-control"
            placeholder="Search"
            // value={advancedPathFilters.textSearch ?? ''}
            onChange={(evt) => updateTextSearch(evt.target.value)}
          />
        </div>
        {lockedTsps.length === 0 && (
          <div className="col">
            <MultiSelectSearch
              placeholder="Pipelines"
              options={tspOptions}
              value={advancedPathFilters.tsps}
              onChange={(options) =>
                dispatch(
                  advancedFiltersActions.setFilterValue({
                    type: 'pathComponents',
                    field: 'tsps',
                    value: options.map((option) => option.value),
                  }),
                )
              }
            />
          </div>
        )}
        <div className="col">
          <MultiSelectSearch
            placeholder="Types"
            options={typeOptions}
            value={advancedPathFilters.types}
            onChange={(options) =>
              dispatch(
                advancedFiltersActions.setFilterValue({
                  type: 'pathComponents',
                  field: 'types',
                  value: options.map((option) => option.value),
                }),
              )
            }
          />
        </div>
      </div>
    </div>
  );
});
